import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai/utils';

import fetchExaCare from '@/adapters/fetchExaCare';
import { queryClient } from '@/adapters/query';
import {
  ExternalEhrFacility,
  Facility,
  FacilityAlternativeName
} from '@/types/crm-screener';
import { atomWithSession } from '@/utils/sessionStorageAtom';

import { TimezonePayload } from './useTimezonesQuery';

// When the selectedFacilityIdAtom initializes to null, it means we should
// initialize it to the user's facility_id of their db row. We shouldn't assume
// that null signifies all facilities selected, otherwise users lower than L1
// would see their facility selected as all facilities. The ALL_FACILITIES const
// below signals downstream queries to not apply the facility search param
export const ALL_FACILITIES = 'ALL_FACILITIES';

// - null, initial value to be set to user's facility_id
// - string, facility_id
// - ALL_FACILITIES, special identifier for all facilities selection
export const selectedFacilityIdAtom = atomWithSession<
  null | string | typeof ALL_FACILITIES
>('selectedFacilityIdAtom', null);

export const useSelectedFacilityId = () => {
  return useAtomValue(selectedFacilityIdAtom);
};

export interface FindAllFacilitiesResponse {
  can_select_all_facilities: boolean;
  facilities: FacilityPayload[];
}

export interface FacilityPayload extends Facility {
  address: string;
  id: string;
  email: string;
  name: string;
  organization_id: string;
  state_id: string;
  time_zone_id: string;
  fax_number?: string;
  timezone: TimezonePayload;
  primary_phone?: string;
  seats: {
    rooms: number;
    residents: number;
  };
  screener_no_analysis_facility?: boolean;
  has_fax_integration?: boolean;
  external_ehr_facility?: ExternalEhrFacility;
  temporary_npi?: string;
  alternative_names?: Array<FacilityAlternativeName>;
}

export const USE_FACILITIES_QUERY_KEY = ['useFacilitiesQuery'];

export function useFacilitiesQuery() {
  const invalidate = () => {
    queryClient.invalidateQueries(USE_FACILITIES_QUERY_KEY);
  };

  return {
    invalidate,

    useFindAllOrOne: (
      facilityId?: string | null | typeof ALL_FACILITIES,
      options: UseQueryOptions<FacilityPayload[]> = {}
    ) =>
      useQuery(
        [...USE_FACILITIES_QUERY_KEY, 'findAllOrOne', facilityId],
        async () => {
          if (facilityId === ALL_FACILITIES) {
            const findAllFacilitiesResponse =
              await fetchExaCare.get<FindAllFacilitiesResponse>('/facilities');
            return findAllFacilitiesResponse.facilities;
          } else {
            const payload = await fetchExaCare.get<FacilityPayload>(
              `/facilities/${facilityId}`
            );
            return [payload];
          }
        },
        {
          enabled: !!facilityId,
          ...(options as any)
        }
      ),

    useFindAll: (options: UseQueryOptions<FacilityPayload[]> = {}) => {
      return useQuery(
        USE_FACILITIES_QUERY_KEY,
        async () => {
          const response =
            await fetchExaCare.get<FindAllFacilitiesResponse>('/facilities');
          return response.facilities;
        },
        options as any
      );
    },

    useFindAllWithCanSelectAllFlag: (
      options: UseQueryOptions<FindAllFacilitiesResponse> = {}
    ) => {
      return useQuery(
        [...USE_FACILITIES_QUERY_KEY, 'findAllWithCanSelectAllFlag'],
        async () => {
          const response =
            await fetchExaCare.get<FindAllFacilitiesResponse>('/facilities');
          return response;
        },
        options as any
      );
    },
    useFindOne: (
      facilityId?: string | null,
      options: UseQueryOptions<FacilityPayload> = {}
    ) => {
      return useQuery(
        [...USE_FACILITIES_QUERY_KEY, facilityId],
        () => {
          return fetchExaCare.get<FacilityPayload>(`/facilities/${facilityId}`);
        },
        {
          enabled: facilityId !== ALL_FACILITIES && !!facilityId,
          ...(options as any)
        }
      );
    },

    mutations: {
      update: useMutation(
        (payload: Partial<FacilityPayload>) => {
          return fetchExaCare.put(`/facilities/${payload.id}`, payload);
        },
        { onSuccess: invalidate }
      )
    }
  };
}
